import React from "react";
import {Box, IconButton, Tooltip, Typography} from "@mui/material";

import ReportChart from "./ReportChart";
import {BarChartOutlined} from "@mui/icons-material";

import {Blue3, Grey6, Grey17} from "../../theme/styles";

import {TileResult, chartForTile} from "./dashboardApi";

/**
 * One tile of a dashboard: a heading, and what its report said.
 *
 * THE HEADING IS THERE BEFORE THE ROWS ARE. The server sends each tile's report name beside the
 * reference precisely so this can render immediately — eight boxes headed by UUIDs while a page
 * loads is a page nobody waits for.
 *
 * A REFUSAL IS A TILE, NOT A HOLE. A report whose dataset changed under it says so in its own box,
 * with the server's own words, while the other tiles still draw. A dashboard is a page of
 * independent questions and one bad answer must not take the rest with it.
 *
 * A TABLE TILE IS A LIST, NOT A GRID. The full grid belongs on the builder, where somebody is
 * working; here it would bring paging, column menus and a search box into a box six inches wide.
 * The tile shows the first rows and says how many there are — the report itself is one click away.
 */

/** How many rows a table tile shows before it stops and says how many more there are. */
const TILE_ROWS = 6;

export default function DashboardTile({tile, loading, onChangeChart}: {
    tile: TileResult; loading?: boolean;
    /** Absent on a dashboard nobody is editing -- the control is then not offered at all. */
    onChangeChart?: (tile: TileResult) => void;
}) {
    const heading = tile.Title || tile.ReportName || "Untitled";
    const full = tile.Width >= 2;
    const drawn = tile.ChartKind !== "table" && !!tile.Columns?.length && !tile.Refusal;

    return (
        <Box sx={{flex: full ? "1 1 100%" : "1 1 calc(50% - 12px)", minWidth: 320,
                  border: "1px solid #E4E7EC", borderRadius: "8px", overflow: "hidden",
                  backgroundColor: "#FFFFFF"}}>
            <Box sx={{px: 2, py: 1.25, borderBottom: "1px solid #E4E7EC",
                      backgroundColor: "#FAFBFC", display: "flex", alignItems: "baseline", gap: 1}}>
                <Typography sx={{fontSize: 13.5, fontWeight: 600, color: Grey17}}>{heading}</Typography>
                {tile.Title && tile.ReportName ? (
                    // NAMED WHEN IT IS RENAMED. A tile headed something other than its report is a
                    // tile somebody will otherwise struggle to trace back to the report it draws.
                    <Typography sx={{fontSize: 11.5, color: Grey6}}>from {tile.ReportName}</Typography>
                ) : null}

                <div style={{flexGrow: 1}}/>

                {/* SAID, NOT LEFT TO BE NOTICED. A tile drawn differently from its report is the
                    confusing state -- somebody changes the report's chart and this one does not
                    follow. It says so, quietly, rather than being silently out of step. */}
                {tile.TileChartSpecification ? (
                    <Tooltip title="This tile has its own chart, so it does not follow the report">
                        <Typography sx={{fontSize: 11, color: Grey6, whiteSpace: "nowrap"}}>
                            own chart
                        </Typography>
                    </Tooltip>
                ) : null}

                {/* A REAL IconButton, not an icon with role="button".
                    Two attempts went into naming a bare SvgIcon: aria-label on it does not name an
                    <svg>, titleAccess does but a wrapping Tooltip then overrides it, and the
                    control ended up with NO accessible name -- unreachable by a screen reader and
                    by getByRole. A button element with aria-label is unambiguous, and it is
                    keyboard-focusable, which an svg with role="button" is not. */}
                {onChangeChart && drawn ? (
                    <Tooltip title="Change how this tile is drawn">
                        <IconButton size="small" aria-label={`Change the chart on ${heading}`}
                                    onClick={() => onChangeChart(tile)}
                                    sx={{p: 0.25, color: Blue3}}>
                            <BarChartOutlined sx={{fontSize: 16}}/>
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Box>

            <Box sx={{p: 2, minHeight: 180}}>
                {loading ? (
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>Running…</Typography>
                ) : tile.Refusal ? (
                    <Typography sx={{fontSize: 12.5, color: "#B42318"}}>{tile.Refusal}</Typography>
                ) : !tile.Columns || !tile.Rows ? (
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>Nothing yet.</Typography>
                ) : tile.Rows.length === 0 ? (
                    // EMPTY IS AN ANSWER, and a different one from broken. Said in words, because an
                    // empty box is indistinguishable from a tile that failed to draw.
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        No rows your persona can see.
                    </Typography>
                ) : tile.ChartKind === "table" ? (
                    <TileTable columns={tile.Columns} rows={tile.Rows} count={tile.RowCount ?? 0}/>
                ) : (
                    // THIS TILE'S CHART, THEN THE REPORT'S, THEN THE DEFAULT -- decided in one
                    // place, in chartForTile, because the precedence IS the feature.
                    <ReportChart spec={chartForTile(tile, tile.Columns)}
                                 columns={tile.Columns} rows={tile.Rows} height={200}/>
                )}
            </Box>
        </Box>
    );
}

function TileTable({columns, rows, count}: {columns: any[]; rows: unknown[][]; count: number}) {
    const shown = rows.slice(0, TILE_ROWS);
    return (
        <Box>
            <Box component="table" sx={{width: "100%", borderCollapse: "collapse", fontSize: 12.5}}>
                <Box component="thead">
                    <Box component="tr">
                        {columns.map((column, index) => (
                            <Box component="th" key={index}
                                 sx={{textAlign: "left", color: Blue3, fontWeight: 600,
                                      fontSize: 11, textTransform: "uppercase",
                                      letterSpacing: "0.04em", pb: 0.75,
                                      borderBottom: "1px solid #E4E7EC"}}>
                                {column.Label}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {shown.map((row, rowIndex) => (
                        <Box component="tr" key={rowIndex}>
                            {row.map((value, index) => (
                                <Box component="td" key={index}
                                     sx={{py: 0.6, color: Grey17, borderBottom: "1px solid #F2F4F7",
                                          whiteSpace: "nowrap", overflow: "hidden",
                                          textOverflow: "ellipsis", maxWidth: 220}}>
                                    {value === null || value === undefined ? "—" : String(value)}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
            {count > shown.length ? (
                // SAYING WHAT IS NOT SHOWN. A tile that silently stops at six rows is a tile
                // somebody quotes the total from.
                <Typography sx={{fontSize: 11.5, color: Grey6, mt: 1}}>
                    Showing {shown.length} of {count} rows.
                </Typography>
            ) : null}
        </Box>
    );
}
